import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagePlayers() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [number, setNumber] = useState('');
  const [filterTeamId, setFilterTeamId] = useState('all');
  const [search, setSearch] = useState('');

  const fetchPlayers = async () => {
    try {
      const { data } = await axios.get(`${API}/players`);
      setPlayers(data);
    } catch (error) {
      toast.error('Failed to load players');
    }
  };

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeams(data);
      if (data.length > 0 && !teamId) setTeamId(data[0].id);
    } catch (error) {
      toast.error('Failed to load teams');
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchPlayers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamId) {
      toast.error('Please create a team first');
      return;
    }
    try {
      await axios.post(
        `${API}/players`,
        { name, team_id: teamId, number: number ? parseInt(number) : null },
        { withCredentials: true }
      );
      toast.success('Player added');
      setName('');
      setNumber('');
      fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    }
  };

  const handleDelete = async (playerId, playerName) => {
    if (!window.confirm(`Delete player "${playerName}"?`)) return;
    try {
      await axios.delete(`${API}/players/${playerId}`, { withCredentials: true });
      toast.success('Player deleted');
      fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete player');
    }
  };

  const filteredPlayers = players.filter((p) => {
    if (filterTeamId !== 'all' && p.team_id !== filterTeamId) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#D4AF37]" />
          Add New Player
        </h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-white mb-2">Player Name</Label>
            <Input
              data-testid="player-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-[#0A0F1C] border-white/20 text-white"
              placeholder="John Smith"
            />
          </div>
          <div>
            <Label className="text-white mb-2">Team</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger data-testid="player-team-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue placeholder="Select team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} ({t.age_group})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white mb-2">Number (optional)</Label>
            <Input
              data-testid="player-number-input"
              type="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="bg-[#0A0F1C] border-white/20 text-white"
              placeholder="10"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              data-testid="create-player-button"
              className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
            >
              Add Player
            </Button>
          </div>
        </form>
      </Card>

      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-xl font-bold text-white">All Players ({filteredPlayers.length})</h3>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9BA3AF]" />
              <Input
                data-testid="player-search-input"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[#0A0F1C] border-white/20 text-white pl-9 w-56"
              />
            </div>
            <Select value={filterTeamId} onValueChange={setFilterTeamId}>
              <SelectTrigger data-testid="filter-players-team" className="w-56 bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} ({t.age_group})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlayers.map((player) => (
            <div
              key={player.id}
              data-testid={`player-card-${player.id}`}
              className="bg-[#0A0F1C] border border-white/5 rounded-xl p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold truncate">
                    {player.number && <span className="text-[#D4AF37] mr-2">#{player.number}</span>}
                    {player.name}
                  </p>
                  <p className="text-[#9BA3AF] text-sm truncate">{player.team_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#D4AF37] font-bold text-xl font-mono">{player.goals}</p>
                  <p className="text-[#9BA3AF] text-xs">Goals</p>
                </div>
                <Button
                  data-testid={`delete-player-${player.id}`}
                  onClick={() => handleDelete(player.id, player.name)}
                  size="sm"
                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
